import { color } from "lib/theme/theme.css"
import { colours } from "@shared/ui.design-system"

export const tagSwitch = (tagType: string): string => {
  switch (tagType) {
    case "Header 1":
      return "h1"
    case "Header 2":
      return "h2"
    case "Header 3":
      return "h3"
    case "Header 4":
      return "h4"
    case "Header 5":
      return "h5"
    case "Paragraph":
      return "p"
    case "Span":
      return "span"
    default:
      return "p"
  }
}

const colorMap: { [key: string]: string } = {
  White: color.background,
  Black: color.content,
  Pink: colours.accent.strong,
  Red: color.contentRed,
  "Red - Dark": color.contentDeepRed,
  "Red on White": color.contentRed,
  "Red - Dark on White": color.contentDeepRed,
  "White on Red": color.background,
  "White on Red - Dark": color.background,
  Yellow: color.contentAccentSecondary,
  "Black on White": color.content,
  "White on Black": color.background,
  Green: color.contentGreen,
  "Green on White": color.contentGreen,
  "White on Green": color.background,
  Orange: color.contentOrange,
  "Orange on White": color.contentOrange,
  "White on Orange": color.background,
  "Pink on White": colours.accent.strong,
  "White on Pink": color.background,
  "White on Yellow": color.background,
  "Yellow on White": color.contentAccentSecondary,
}

export const colorSwitch = (textColor: string): string => {
  return colorMap[textColor] || color.content
}

const spanColorMap: { [key: string]: string } = {
  White: "none",
  Black: "none",
  Pink: "none",
  Red: "none",
  Yellow: "none",
  "Black on White": color.background,
  "White on Black": color.content,
  "Green on White": color.background,
  "White on Green": color.contentGreen,
  "White on Orange": color.contentOrange,
  "Orange on White": color.background,
  "Red on White": color.background,
  "White on Red": color.contentRed,
  "Pink on White": color.background,
  "White on Pink": colours.accent.strong,
  "White on Yellow": color.contentAccentSecondary,
  "Yellow on White": color.background,
  "Red - Dark on White": color.background,
  "White on Red - Dark": color.contentDeepRed,
}

const spanColor = (textColor: string): string => {
  return spanColorMap[textColor] || "none"
}

type SpanStyleType = {
  background: string
  padding: string
  boxDec: string
}

export const getSpanStyles = (fontColor: string): SpanStyleType => {
  const padCheck = fontColor.includes(" on ")
  return {
    background: fontColor ? spanColor(fontColor) : "none",
    padding: padCheck ? "0.16em 0.4em 0.08em 0.4em" : "none",
    boxDec: padCheck ? "clone" : "none",
  }
}

type FontSizeOption = {
  min: number
  pref: number
  max: number
}

// Leaving sizes 15-19 as the same as 14 for now with scope for adding more when needed
const fontSizesDesktop = [
  { min: 6.6, pref: 0.86, max: 12 }, // 1
  { min: 8.5, pref: 1.11, max: 16 }, // 2
  { min: 9.6, pref: 1.25, max: 18 }, // 3
  { min: 12.8, pref: 1.67, max: 24 }, // 4
  { min: 17, pref: 2.22, max: 32 }, // 5
  { min: 21.35, pref: 2.78, max: 40 }, // 6
  { min: 25.7, pref: 3.34, max: 48 }, // 7
  { min: 29.6, pref: 3.9, max: 56 }, // 8
  { min: 34.2, pref: 4.45, max: 64 }, // 9
  { min: 38.4, pref: 5, max: 72 }, // 10
  { min: 43, pref: 5.5, max: 80 }, // 11
  { min: 47, pref: 6.1, max: 88 }, // 12
  { min: 51.2, pref: 6.67, max: 96 }, // 13
  { min: 53, pref: 6.95, max: 100 }, // 14
  { min: 53, pref: 6.95, max: 100 }, // 15
  { min: 53, pref: 6.95, max: 100 }, // 16
  { min: 53, pref: 6.95, max: 100 }, // 17
  { min: 53, pref: 6.95, max: 100 }, // 18
  { min: 53, pref: 6.95, max: 100 }, // 19
  { min: 75, pref: 9.75, max: 150 }, // 20
] as FontSizeOption[]

export const fontSizesMobile = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 12, 14, 16, 18]

export const getFontSize = (fontSize: number): string => {
  let fontSizeToGet = fontSize - 1
  if (fontSize >= fontSizesDesktop.length) {
    fontSizeToGet = fontSizesDesktop.length - 1
  }
  if (fontSize <= 1) {
    fontSizeToGet = 0
  }
  return `clamp(${fontSizesDesktop[fontSizeToGet].min}px, ${fontSizesDesktop[fontSizeToGet].pref}vw, ${fontSizesDesktop[fontSizeToGet].max}px)`
}

const switchLineHeight = (lineHeight: string): number => {
  switch (lineHeight) {
    case "Very Very Tight":
      return 0.75
    case "Very Tight":
      return 1
    case "Tight":
      return 1.25
    case "Normal":
      return 1.5
    case "Loose":
      return 1.75
    case "Very Loose":
      return 2
    default:
      return 1.5
  }
}

export const getLineHeight = (
  fontSize: number,
  lineHeight: string,
  device: "desktop" | "mobile"
): string => {
  const multiplier = switchLineHeight(lineHeight)
  if (device === "desktop") {
    let fontSizeToGet = fontSize - 1
    if (fontSize > fontSizesDesktop.length) {
      fontSizeToGet = fontSizesDesktop.length - 1
    }
    if (fontSize <= 1) {
      fontSizeToGet = 0
    }
    const fontSizes = fontSizesDesktop[fontSizeToGet]
    const { min, pref, max } = fontSizes
    return `clamp(${min * multiplier}px, ${pref * multiplier}vw, ${
      max * multiplier
    }px)`
  }
  return `${multiplier * fontSizesMobile[fontSize]}vw`
}

export const switchLetterSpacing = (letterSpacing: string): string => {
  switch (letterSpacing) {
    case "Very Tight":
      return "-0.1em"
    case "Tight":
      return "-0.05em"
    case "Normal":
      return "normal"
    case "Loose":
      return "0.05em"
    case "Very Loose":
      return "0.1em"
    case "Very Very Loose":
      return "0.2em"
    case "Very Very Very Loose":
      return "0.3em"
    default:
      return "normal"
  }
}
